import { type RNG, defaultRng, randInt } from "./rng";

/**
 * Reservoir sampling keeps a uniform random sample of k items from a stream
 * whose length you don't know in advance, in a single pass and O(k) memory.
 *
 * This is Vitter's "Algorithm R": the first k items fill the reservoir; each
 * later item (the n-th, n > k) replaces a random reservoir slot with
 * probability k/n. A short induction shows every item seen so far is in the
 * reservoir with probability exactly k/n at all times — a genuinely uniform
 * sample.
 */
export class ReservoirSampler<T> {
  readonly capacity: number;
  private readonly reservoir: T[] = [];
  private readonly rng: RNG;
  /** How many items have been offered to the sampler. */
  seen = 0;

  constructor(capacity: number, rng: RNG = defaultRng) {
    if (!Number.isInteger(capacity) || capacity <= 0)
      throw new RangeError("capacity must be a positive integer");
    this.capacity = capacity;
    this.rng = rng;
  }

  /**
   * Offer one item to the sampler. Returns true if it currently sits in the
   * reservoir (handy for visualising accept/reject decisions).
   */
  add(item: T): boolean {
    this.seen++;
    if (this.reservoir.length < this.capacity) {
      this.reservoir.push(item);
      return true;
    }
    const j = randInt(this.rng, this.seen);
    if (j < this.capacity) {
      this.reservoir[j] = item;
      return true;
    }
    return false;
  }

  /** The current uniform sample (a copy). */
  sample(): T[] {
    return this.reservoir.slice();
  }

  /** True once the reservoir has filled to capacity. */
  isFull(): boolean {
    return this.reservoir.length >= this.capacity;
  }

  /** Probability that the *next* item would be admitted into the reservoir. */
  nextAdmitProbability(): number {
    if (this.reservoir.length < this.capacity) return 1;
    return this.capacity / (this.seen + 1);
  }
}
